import * as React from 'react';
import {
  ITsaReportState,
  tsaReportProvider,
} from '../../../data/providers/reports/tsaReportProvider';
import { Entity, EntityType } from '../../../data/types';
import { Column } from '../../layout/Column';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import { EPdfMode, isPdf, Pdf } from '../../shared/pdf/Pdf';
import { PdfContainer } from '../../shared/pdf/PdfContainer';
import { ErrorMessage } from '../utils/ErrorMessages';
import { quartileColors } from '../utils/quartileColors';
import { ReportOverview } from '../utils/ReportOverview';
import { useFilename } from '../utils/useFilename';
import { TsaGraphCard } from './TsaGraphCard';
import { ResultList } from './ResultList';
import { TsaSettings } from './settings/Settings';

interface IProps {
  pdfMode?: EPdfMode;
  entity: Entity;
  entityType: EntityType;
  onPdfModeChange: (pdfMode: EPdfMode) => void;
  loading: boolean;
  setLoading: (v: boolean) => void;
}

export const TsaReport = (props: IProps): React.ReactElement => {
  const [reportState, setReportState] = React.useState<ITsaReportState>();
  /**
   * @description subscribe to data provider
   */
  React.useEffect((): (() => void) => {
    props.setLoading(true);
    const subscriber = tsaReportProvider(
      props.entityType,
      props.entity
    ).subscribe((state: ITsaReportState): void => {
      setReportState(state);
      props.setLoading(false);
    });
    return (): void => subscriber.unsubscribe();
  }, [props.entity?.id]);

  /**
   * @description make filename for export
   */
  const filename = useFilename('tsa', props.entity, reportState?.timeframe);

  return (
    <React.Fragment>
      {reportState?.isValid && !props.loading ? (
        isPdf(props.pdfMode) ? (
          <PdfContainer>
            <Pdf
              onComplete={() => {}}
              filename={filename}
              title='TSA Report'
              entity={props.entity}
              loading={props.loading}
              reportState={reportState}
              onPdfModeChange={props.onPdfModeChange}
              pdfMode={props.pdfMode}
            >
              <Column>
                <ReportOverview
                  populationTimeframe={{
                    dateType: reportState.comparisonPopulation?.dateRangeType,
                    dateRange:
                      reportState.comparisonPopulation?.dateRangeValues,
                  }}
                  populationEntityName={
                    reportState.comparisonPopulation?.entity?.label
                  }
                  targetTimeframe={{
                    dateType: reportState?.targetTimeframe?.dateType,
                    dateRange: reportState?.targetTimeframe?.dateRange,
                  }}
                  targetEntity={props.entity}
                  reportName={`TSA Report - ${reportState?.settings?.name}`}
                  isPdf={isPdf(props.pdfMode)}
                />
                <TsaGraphCard
                  entity={props.entity}
                  reportState={reportState}
                  pdfMode={props.pdfMode}
                  quartileColors={quartileColors}
                />
                <ResultList
                  reportState={reportState}
                  colors={quartileColors}
                  pdfMode={props.pdfMode}
                  entity={props.entity}
                />
              </Column>
            </Pdf>
          </PdfContainer>
        ) : (
          <Column>
            {!props.loading ? (
              <React.Fragment>
                <ReportOverview
                  populationTimeframe={{
                    dateType: reportState.comparisonPopulation?.dateRangeType,
                    dateRange:
                      reportState.comparisonPopulation?.dateRangeValues,
                  }}
                  populationEntityName={
                    reportState.comparisonPopulation?.entity?.label
                  }
                  targetTimeframe={{
                    dateType: reportState?.targetTimeframe?.dateType,
                    dateRange: reportState?.targetTimeframe?.dateRange,
                  }}
                  targetEntity={props.entity}
                  reportName={`TSA Report - ${reportState?.settings?.name}`}
                  isPdf={isPdf(props.pdfMode)}
                />
                <TsaGraphCard
                  entity={props.entity}
                  reportState={reportState}
                  pdfMode={props.pdfMode}
                  quartileColors={quartileColors}
                />
                <ResultList
                  reportState={reportState}
                  colors={quartileColors}
                  pdfMode={props.pdfMode}
                  entity={props.entity}
                />
              </React.Fragment>
            ) : (
              <LinearLoading />
            )}
          </Column>
        )
      ) : props.loading ? (
        <LinearLoading />
      ) : (
        <Error
          errorCode={reportState?.errorCode}
          setLoading={props.setLoading}
        />
      )}
    </React.Fragment>
  );
};

const Error = ({
  errorCode,
  setLoading,
}: {
  errorCode?: string;
  setLoading: (v: boolean) => void;
}): React.ReactElement => {
  const onSave = (): void => setLoading(true);
  switch (errorCode) {
    case 'missing-template':
      return (
        <ErrorMessage iconName='window' text='Please Select a Template'>
          <TsaSettings
            style={{ marginTop: '10px', maxWidth: 'max-content' }}
            onSave={onSave}
          />
        </ErrorMessage>
      );
    case 'invalid-date-range':
      return (
        <ErrorMessage iconName='date_range' text='Missing Date Range'>
          <div style={{ marginTop: '10px' }}>
            Please select a date range for this report.
          </div>
          <TsaSettings style={{ marginTop: '10px' }} onSave={onSave} />
        </ErrorMessage>
      );
    case 'insufficient-data':
      return (
        <ErrorMessage iconName='error_outline' text='Not Enough Data'>
          <div style={{ marginTop: '10px' }}>
            There are not enough data points to produce this report. Try
            changing the timeframe in settings
          </div>
          <TsaSettings style={{ marginTop: '10px' }} onSave={onSave} />
        </ErrorMessage>
      );

    default:
      return null;
  }
};
